import { Add as AddIcon, Delete as DeleteIcon } from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import JalaliDatePicker from "../../../../components/shared/jalali-datepicker";
import {
  CALCULATION_TYPE_LABELS,
  LOAN_STATUS_LABELS,
  PAYMENT_STATUS_LABELS,
  PERCENT_BASE_LABELS,
  TRIGGER_EVENT_LABELS,
  isTieredCalculation,
  toSelectOptions,
} from "../constants/commissionEnums";
import {
  formatApiDate,
  parseOptionalNumber,
  validateRuleForm,
} from "../utils/commissionValidation";

const emptyTier = () => ({
  minCapacityKw: "",
  maxCapacityKw: "",
  fixedAmount: "",
  percentValue: "",
});

const emptyForm = () => ({
  commissionGroupId: "",
  title: "",
  triggerEvent: "UserRegistered",
  calculationType: "FixedAmount",
  percentBase: "",
  fixedAmount: "",
  percentValue: "",
  requiredProjectStage: "",
  requiredPaymentStatus: "",
  requiredLoanStatus: "",
  minCapacityKw: "",
  maxCapacityKw: "",
  priority: 1,
  isActive: true,
  validFrom: new Date(),
  validTo: null,
  tiers: [],
});

const CommissionRuleFormDialog = ({
  open,
  onClose,
  onSubmit,
  initialData,
  groups,
  saving,
}) => {
  const [form, setForm] = useState(emptyForm());
  const [errors, setErrors] = useState({});
  const [submitError, setSubmitError] = useState(null);
  const isEdit = Boolean(initialData?.id);
  const tiered = isTieredCalculation(form.calculationType);

  useEffect(() => {
    if (!open) return;
    setSubmitError(null);
    setErrors({});

    if (initialData) {
      setForm({
        commissionGroupId: initialData.commissionGroupId || "",
        title: initialData.title || "",
        triggerEvent: initialData.triggerEvent || "UserRegistered",
        calculationType: initialData.calculationType || "FixedAmount",
        percentBase: initialData.percentBase || "",
        fixedAmount: initialData.fixedAmount ?? "",
        percentValue: initialData.percentValue ?? "",
        requiredProjectStage: initialData.requiredProjectStage || "",
        requiredPaymentStatus: initialData.requiredPaymentStatus || "",
        requiredLoanStatus: initialData.requiredLoanStatus || "",
        minCapacityKw: initialData.minCapacityKw ?? "",
        maxCapacityKw: initialData.maxCapacityKw ?? "",
        priority: initialData.priority ?? 1,
        isActive: initialData.isActive ?? true,
        validFrom: initialData.validFrom ? new Date(initialData.validFrom) : new Date(),
        validTo: initialData.validTo ? new Date(initialData.validTo) : null,
        tiers: (initialData.tiers || []).map((tier) => ({
          minCapacityKw: tier.minCapacityKw ?? "",
          maxCapacityKw: tier.maxCapacityKw ?? "",
          fixedAmount: tier.fixedAmount ?? "",
          percentValue: tier.percentValue ?? "",
        })),
      });
    } else {
      setForm(emptyForm());
    }
  }, [open, initialData]);

  const handleChange = (field, value) => {
    setForm((prev) => {
      const next = { ...prev, [field]: value };
      if (field === "calculationType") {
        if (isTieredCalculation(value) && !prev.tiers.length) {
          next.tiers = [emptyTier()];
        }
        if (!isTieredCalculation(value)) {
          next.tiers = [];
        }
      }
      return next;
    });
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: null }));
    if (submitError) setSubmitError(null);
  };

  const handleTierChange = (index, field, value) => {
    setForm((prev) => {
      const tiers = [...prev.tiers];
      tiers[index] = { ...tiers[index], [field]: value };
      return { ...prev, tiers };
    });
    const key = `tier_${index}_${field}`;
    if (errors[key]) setErrors((prev) => ({ ...prev, [key]: null }));
    if (errors.tiers) setErrors((prev) => ({ ...prev, tiers: null }));
  };

  const addTier = () => {
    setForm((prev) => ({ ...prev, tiers: [...prev.tiers, emptyTier()] }));
  };

  const removeTier = (index) => {
    setForm((prev) => ({
      ...prev,
      tiers: prev.tiers.filter((_, i) => i !== index),
    }));
  };

  const buildPayload = () => {
    const payload = {
      commissionGroupId: Number(form.commissionGroupId),
      title: form.title.trim(),
      triggerEvent: form.triggerEvent,
      calculationType: form.calculationType,
      percentBase: form.percentBase || null,
      fixedAmount: parseOptionalNumber(form.fixedAmount),
      percentValue: parseOptionalNumber(form.percentValue),
      requiredProjectStage: form.requiredProjectStage?.trim() || null,
      requiredPaymentStatus: form.requiredPaymentStatus || null,
      requiredLoanStatus: form.requiredLoanStatus || null,
      minCapacityKw: parseOptionalNumber(form.minCapacityKw),
      maxCapacityKw: parseOptionalNumber(form.maxCapacityKw),
      priority: Number(form.priority),
      isActive: form.isActive,
      validFrom: formatApiDate(form.validFrom),
      validTo: form.validTo ? formatApiDate(form.validTo) : null,
      tiers: [],
    };

    if (tiered) {
      payload.tiers = form.tiers.map((tier) => ({
        minCapacityKw: parseOptionalNumber(tier.minCapacityKw),
        maxCapacityKw: parseOptionalNumber(tier.maxCapacityKw),
        fixedAmount:
          form.calculationType === "TieredFixedAmount"
            ? parseOptionalNumber(tier.fixedAmount)
            : null,
        percentValue:
          form.calculationType === "TieredPercentage"
            ? parseOptionalNumber(tier.percentValue)
            : null,
      }));
      payload.fixedAmount = null;
      payload.percentValue = null;
      payload.minCapacityKw = null;
      payload.maxCapacityKw = null;
    }

    if (form.calculationType === "ManualApproval") {
      payload.fixedAmount = null;
      payload.percentValue = null;
      payload.percentBase = null;
    }

    if (form.calculationType === "FixedAmount") {
      payload.percentBase = null;
      payload.percentValue = null;
    }

    return payload;
  };

  const handleSubmit = async () => {
    const validationErrors = validateRuleForm(form);
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    const result = await onSubmit(buildPayload());
    if (result?.error) setSubmitError(result.error);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{isEdit ? "ویرایش قانون پورسانت" : "ایجاد قانون پورسانت"}</DialogTitle>
      <DialogContent dividers>
        {submitError ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            {submitError}
          </Alert>
        ) : null}

        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth required error={Boolean(errors.commissionGroupId)}>
              <InputLabel>گروه پورسانتی</InputLabel>
              <Select
                value={form.commissionGroupId}
                label="گروه پورسانتی"
                onChange={(e) => handleChange("commissionGroupId", e.target.value)}
              >
                {groups.map((group) => (
                  <MenuItem key={group.id} value={group.id}>
                    {group.title} ({group.code})
                  </MenuItem>
                ))}
              </Select>
              {errors.commissionGroupId ? (
                <Typography variant="caption" color="error">
                  {errors.commissionGroupId}
                </Typography>
              ) : null}
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="عنوان"
              value={form.title}
              onChange={(e) => handleChange("title", e.target.value)}
              fullWidth
              required
              error={Boolean(errors.title)}
              helperText={errors.title}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth required error={Boolean(errors.triggerEvent)}>
              <InputLabel>رویداد</InputLabel>
              <Select
                value={form.triggerEvent}
                label="رویداد"
                onChange={(e) => handleChange("triggerEvent", e.target.value)}
              >
                {toSelectOptions(TRIGGER_EVENT_LABELS).map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth required error={Boolean(errors.calculationType)}>
              <InputLabel>نوع محاسبه</InputLabel>
              <Select
                value={form.calculationType}
                label="نوع محاسبه"
                onChange={(e) => handleChange("calculationType", e.target.value)}
              >
                {toSelectOptions(CALCULATION_TYPE_LABELS).map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {(form.calculationType === "Percentage" ||
            form.calculationType === "TieredPercentage") && (
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth required error={Boolean(errors.percentBase)}>
                <InputLabel>مبنای درصد</InputLabel>
                <Select
                  value={form.percentBase}
                  label="مبنای درصد"
                  onChange={(e) => handleChange("percentBase", e.target.value)}
                >
                  {toSelectOptions(PERCENT_BASE_LABELS).map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
                {errors.percentBase ? (
                  <Typography variant="caption" color="error">
                    {errors.percentBase}
                  </Typography>
                ) : null}
              </FormControl>
            </Grid>
          )}

          {form.calculationType === "FixedAmount" && (
            <Grid item xs={12} sm={6}>
              <TextField
                label="مبلغ ثابت"
                type="number"
                value={form.fixedAmount}
                onChange={(e) => handleChange("fixedAmount", e.target.value)}
                fullWidth
                required
                error={Boolean(errors.fixedAmount)}
                helperText={errors.fixedAmount}
              />
            </Grid>
          )}

          {form.calculationType === "Percentage" && (
            <Grid item xs={12} sm={6}>
              <TextField
                label="درصد"
                type="number"
                value={form.percentValue}
                onChange={(e) => handleChange("percentValue", e.target.value)}
                fullWidth
                required
                error={Boolean(errors.percentValue)}
                helperText={errors.percentValue}
              />
            </Grid>
          )}

          {!tiered && form.calculationType !== "ManualApproval" && (
            <>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="حداقل ظرفیت (kW)"
                  type="number"
                  value={form.minCapacityKw}
                  onChange={(e) => handleChange("minCapacityKw", e.target.value)}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="حداکثر ظرفیت (kW)"
                  type="number"
                  value={form.maxCapacityKw}
                  onChange={(e) => handleChange("maxCapacityKw", e.target.value)}
                  fullWidth
                />
              </Grid>
            </>
          )}

          <Grid item xs={12} sm={4}>
            <TextField
              label="اولویت"
              type="number"
              value={form.priority}
              onChange={(e) => handleChange("priority", e.target.value)}
              fullWidth
              required
              error={Boolean(errors.priority)}
              helperText={errors.priority}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <FormControlLabel
              control={
                <Switch
                  checked={form.isActive}
                  onChange={(e) => handleChange("isActive", e.target.checked)}
                />
              }
              label="فعال"
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              label="مرحله پروژه موردنیاز"
              value={form.requiredProjectStage}
              onChange={(e) => handleChange("requiredProjectStage", e.target.value)}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <FormControl fullWidth>
              <InputLabel>وضعیت پرداخت</InputLabel>
              <Select
                value={form.requiredPaymentStatus}
                label="وضعیت پرداخت"
                onChange={(e) => handleChange("requiredPaymentStatus", e.target.value)}
              >
                <MenuItem value="">—</MenuItem>
                {toSelectOptions(PAYMENT_STATUS_LABELS).map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={3}>
            <FormControl fullWidth>
              <InputLabel>وضعیت وام</InputLabel>
              <Select
                value={form.requiredLoanStatus}
                label="وضعیت وام"
                onChange={(e) => handleChange("requiredLoanStatus", e.target.value)}
              >
                <MenuItem value="">—</MenuItem>
                {toSelectOptions(LOAN_STATUS_LABELS).map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6}>
            <JalaliDatePicker
              label="تاریخ شروع"
              value={form.validFrom}
              onChange={(value) => handleChange("validFrom", value ? new Date(value) : null)}
              fullWidth
              error={Boolean(errors.validFrom)}
              helperText={errors.validFrom}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <JalaliDatePicker
              label="تاریخ پایان"
              value={form.validTo}
              onChange={(value) => handleChange("validTo", value ? new Date(value) : null)}
              fullWidth
              error={Boolean(errors.validTo)}
              helperText={errors.validTo}
            />
          </Grid>

          {tiered ? (
            <Grid item xs={12}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  mb: 1,
                }}
              >
                <Typography variant="subtitle2">پله‌های محاسبه</Typography>
                <Button size="small" startIcon={<AddIcon />} onClick={addTier}>
                  افزودن پله
                </Button>
              </Box>
              {errors.tiers ? (
                <Alert severity="error" sx={{ mb: 1 }}>
                  {errors.tiers}
                </Alert>
              ) : null}
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>حداقل kW</TableCell>
                    <TableCell>حداکثر kW</TableCell>
                    {form.calculationType === "TieredFixedAmount" ? (
                      <TableCell>مبلغ ثابت</TableCell>
                    ) : (
                      <TableCell>درصد</TableCell>
                    )}
                    <TableCell align="center">حذف</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {form.tiers.map((tier, index) => (
                    <TableRow key={`tier-${index}`}>
                      <TableCell>
                        <TextField
                          size="small"
                          type="number"
                          value={tier.minCapacityKw}
                          onChange={(e) =>
                            handleTierChange(index, "minCapacityKw", e.target.value)
                          }
                          fullWidth
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          size="small"
                          type="number"
                          value={tier.maxCapacityKw}
                          onChange={(e) =>
                            handleTierChange(index, "maxCapacityKw", e.target.value)
                          }
                          fullWidth
                          placeholder="نامحدود"
                        />
                      </TableCell>
                      <TableCell>
                        {form.calculationType === "TieredFixedAmount" ? (
                          <TextField
                            size="small"
                            type="number"
                            value={tier.fixedAmount}
                            onChange={(e) =>
                              handleTierChange(index, "fixedAmount", e.target.value)
                            }
                            fullWidth
                            error={Boolean(errors[`tier_${index}_fixedAmount`])}
                            helperText={errors[`tier_${index}_fixedAmount`]}
                          />
                        ) : (
                          <TextField
                            size="small"
                            type="number"
                            value={tier.percentValue}
                            onChange={(e) =>
                              handleTierChange(index, "percentValue", e.target.value)
                            }
                            fullWidth
                            error={Boolean(errors[`tier_${index}_percentValue`])}
                            helperText={errors[`tier_${index}_percentValue`]}
                          />
                        )}
                      </TableCell>
                      <TableCell align="center">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => removeTier(index)}
                          disabled={form.tiers.length <= 1}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Grid>
          ) : null}
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          انصراف
        </Button>
        <Button variant="contained" onClick={handleSubmit} disabled={saving}>
          {saving ? "در حال ذخیره..." : isEdit ? "ذخیره" : "ایجاد"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CommissionRuleFormDialog;
